import { Box } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey, LayoutToolbarHeight } from "assets/jss/common-styles";
import { SpinnerIndicator } from "components/CallServer";
import { LoadingIndicator } from "components/CallServer/LoadingIndicator";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from "components/Translations/AppMessage";
import { AccessKeys } from "Constants";
import { FeedType, PublicationType, PublicationVisility } from "models/ApiEnums";
import { FeedContentDto, FeedItemDto } from "models/ApiModels";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { AutoSizer, CellMeasurer, CellMeasurerCache, InfiniteLoader, List, WindowScroller } from 'react-virtualized';
import { FeedState } from "store/feed/reducer";
import AnswerCard from "views/Timeline/Components/CardFactory/AnswerCard";
import CourseCard from "views/Timeline/Components/CardFactory/CourseCard";
import HideCard from "views/Timeline/Components/CardFactory/HideCard";
import ListCard from "views/Timeline/Components/CardFactory/ListCard";
import PollCard from "views/Timeline/Components/CardFactory/PollCard";
import PostCard from "views/Timeline/Components/CardFactory/PostCard";
import QuestionCard from "views/Timeline/Components/CardFactory/QuestionCard";
import { ICardHiddenProps, ICardProps } from "views/Timeline/Components/CardLib/ICardProps";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    lastError: any;
}

interface IProps {
    batchSize?: number;

    feed: FeedState;
    feedType: FeedType;

    onAddCard?: (uid: string, publicationId: string) => Promise<void>;
    onRemoveFromFeed: (uid: string, publicationId: string) => void;
    onGetFeed: (startIndex: number, endIndex: number) => Promise<any>;

    onExpandPublication: (uid: string, publicationId: string, textExpanded: boolean) => Promise<void>;
    onPublishPublication?: (uid: string, publicationId: string, publicationType: PublicationType) => Promise<boolean>;
    onReloadPublication: (uid: string, publicationId: string) => Promise<void>;
    onRemovePublication?: (uid: string, publicationId: string, publicationType: PublicationType) => Promise<boolean>;
    onChangePublicationVisibility?: (uid: string, publicationId: string, visibility: PublicationVisility) => Promise<void>;
    onUpdatePublication: (newItemDto: DeepPartial<FeedContentDto> | DeepPartial<FeedContentDto[]>) => void;

    onApprovalRequest?: (uid: string, publicationId: string, actionId: string, approved: boolean, notes: string) => Promise<boolean>;
    onIgnoreActionRequest?: (uid: string, publicationId: string, actionId: string) => Promise<boolean>;

    onCustomAction?: (uid: string, publicationId: string, publicationType: PublicationType, action: any) => Promise<boolean>;

    onBeforeEdit?: (uid: string, publicationId: string) => boolean;
    onBeforeDelete?: (uid: string, item: FeedItemDto) => Promise<boolean>;
    onSidebarResetRequest?: () => void;

    onFeedReset?: () => void;
}

type Props = IProps & StyledComponentProps<FeedClassKey> & WithSnackbar;

const cardSpaceHeight = 10;

/////////////////////////////////////////////////////////////////////////////////////////////////

const FocusOnFirstCard: React.FC = () => {
    const { t } = useTranslation();
    return (
        <Box display="none">
            <a
                accessKey={AccessKeys.FeedFirstCard}
                onClick={
                    (e) => {
                        var g = document.getElementsByClassName("ReactVirtualized__Grid__innerScrollContainer")[0];
                        if (g) {
                            for (var i = 0; i < g.childNodes.length; i++) {
                                var div = g.childNodes[i] as HTMLDivElement;
                                if (parseInt(div.style.top) >= window.scrollY - LayoutToolbarHeight - cardSpaceHeight) {
                                    var a = div.querySelector("a[class~='aCardTitle']") as HTMLLinkElement;
                                    a.focus();
                                    break;
                                }
                            }
                        }
                    }
                }
            >
                <span>{t("Common.AccessKeys.FeedFirstCard")}</span>
            </a>
        </Box>
    );
};

////////////////////////////////////////////////////////////////////////////

const ReloadFeedText: React.FC = () => {
    const { t } = useTranslation();
    return <span>{t("Common.AccessKeys.ReloadFeed")}</span>;
};

////////////////////////////////////////////////////////////////////////////

class CardManager extends React.PureComponent<Props, IState> {
    private listRef: List;
    private infiniteLoaderRef: InfiniteLoader;
    private cache: CellMeasurerCache;

    constructor(props: Props) {
        super(props);
        this.cache = new CellMeasurerCache({ fixedWidth: true, minHeight: 50 });
        this.state = { lastError: null };
    }

    private getBatchSize() {
        return this.props.batchSize || 20;
    }

    private setRef(ref: List, registerChild: any) {
        if (ref) this.listRef = ref;
        registerChild(ref);
    }

    private isRowLoaded(index: number): boolean {
        return index < this.props.feed.data.length;
    }

    private recomputeRowHeight(index: number, allAbove: boolean) {
        if (allAbove) {
            for (var i = index; i < this.props.feed.data.length; i++) {
                this.cache.clear(i, 0);
            }
        }
        else
            this.cache.clear(index, 0);

        this.listRef.recomputeRowHeights(i);
    }

    private rowRenderer(index: any, key: any, parent: any, style: any) {

        if (!this.isRowLoaded(index)) return null;

        const { feed: { data } } = this.props;
        const item = data[index];

        return (
            <CellMeasurer
                key={key}
                cache={this.cache}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                {({ measure }) => (
                    <div style={style} >
                        {this.buildFeedContent(item, index)}
                    </div>
                )}
            </CellMeasurer>
        );
    }

    private handleBeforeDelete(uid: string, item: FeedItemDto, index: number): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.props.onBeforeDelete(uid, item)
                .then((handled) => {
                    if (handled) this.recomputeRowHeight(index, true);
                    resolve(handled);
                })
                .catch((error) => reject(error));
        });
    }

    private handleRemovePublication(uid: string, publicationId: string, publicationType: PublicationType, index: number) {
        return new Promise<boolean>((resolve, reject) => {
            this.props.onRemovePublication(uid, publicationId, publicationType)
                .then((result) => {
                    if (result) this.recomputeRowHeight(index, true);
                    resolve(result);
                })
                .catch((error) => reject(error));
        });
    }

    private handleRemoveCardFromFeed(uid: string, publicationId: string, index: number) {
        this.props.onRemoveFromFeed(uid, publicationId);
        this.recomputeRowHeight(index, true);
    }

    private handlePublishPublication(uid: string, publicationId: string, publicationType: PublicationType, index: number) {
        return new Promise<boolean>((resolve) => {
            this.props.onPublishPublication(uid, publicationId, publicationType)
                .then((result) => {
                    if (result) this.recomputeRowHeight(index, true);
                    resolve(result);
                })
                .catch((error) => {
                    this.setState({ lastError: error });
                });

        });
    }

    private handleChangePublicationVisibility(uid: string, publicationId: string, visibility: PublicationVisility, index: number) {
        return new Promise<void>((resolve) => {
            this.props.onChangePublicationVisibility(uid, publicationId, visibility)
                .then(() => {
                    this.recomputeRowHeight(index, true);
                    resolve();
                })
                .catch((error) => {
                    this.setState({ lastError: error });
                });

        });
    }

    private handleReloadPublication(uid: string, item: FeedItemDto, index: number) {
        return new Promise<void>((resolve) => {
            this.props.onReloadPublication(uid, item.publicationId)
                .then(() => {
                    this.recomputeRowHeight(index, true);
                    resolve();
                })
                .catch((error) => {
                    this.setState({ lastError: error });
                });
        });
    }

    private handleCustomAction(uid: string, item: FeedItemDto, action: any, index: number) {
        return new Promise<boolean>((resolve) => {
            this.props.onCustomAction(uid, item.publicationId, item.publicationType, action)
                .then((result) => {
                    if (result) {
                        if (this.props.onReloadPublication) {
                            this.props.onReloadPublication(uid, item.publicationId)
                                .then(() => {
                                    this.recomputeRowHeight(index, true);
                                    resolve(true);
                                });
                        }
                        else {
                            resolve(false);
                        }
                    }
                    else {
                        this.recomputeRowHeight(index, true);
                        resolve(false);
                    }
                })
                .catch((error) => {
                    this.setState({ lastError: error });
                });
        });
    }

    private cardHidden(uid: string, item: FeedItemDto, index: number) {

        const removeCardFromFeedHandler = this.props.onRemoveFromFeed
            ? () => this.handleRemoveCardFromFeed(uid, item.publicationId, index)
            : null;

        const changePublicationVisibilityHandler = this.props.onChangePublicationVisibility
            ? (visibility: PublicationVisility) => this.handleChangePublicationVisibility(uid, item.publicationId, visibility, index)
            : null;

        const p: ICardHiddenProps = {

            item,

            feedType: this.props.feedType,

            onChangePublicationVisibility: changePublicationVisibilityHandler,

            onRemoveCardFromFeed: removeCardFromFeedHandler,

            onUpdateCard: (item: DeepPartial<FeedItemDto>) => {
                this.props.onUpdatePublication({ uid, items: [item] });
                this.recomputeRowHeight(index, true);
            },

        };

        return <HideCard key={uid} classes={this.props.classes} {...p} />;
    }

    private cardFactory(uid: string, item: FeedItemDto, rowIndex: number) {

        const addCardHandler = this.props.onAddCard
            ? () => this.props.onAddCard(uid, item.publicationId)
            : null;

        const beforeEditHandler = this.props.onBeforeEdit
            ? () => this.props.onBeforeEdit(uid, item.publicationId)
            : null;

        const beforeDeleteHandler = this.props.onBeforeDelete
            ? () => this.handleBeforeDelete(uid, item, rowIndex)
            : null;

        const removePublicationHandler = this.props.onRemovePublication
            ? () => this.handleRemovePublication(uid, item.publicationId, item.publicationType, rowIndex)
            : null;

        const removeCardFromFeedHandler = this.props.onRemoveFromFeed
            ? () => this.handleRemoveCardFromFeed(uid, item.publicationId, rowIndex)
            : null;

        const publishPublicationHandler = this.props.onPublishPublication && item.publication.isDraft === true
            ? () => this.handlePublishPublication(uid, item.publicationId, item.publicationType, rowIndex)
            : null;

        const reloadHandler = this.props.onReloadPublication
            ? () => this.handleReloadPublication(uid, item, rowIndex)
            : null;

        const customActionHandler = this.props.onCustomAction
            ? (action: any) => this.handleCustomAction(uid, item, action, rowIndex)
            : null;

        const changePublicationVisibilityHandler = this.props.onChangePublicationVisibility
            ? (visibility: PublicationVisility) => this.handleChangePublicationVisibility(uid, item.publicationId, visibility, rowIndex)
            : null;

        const approvalRequestHandler = this.props.onApprovalRequest
            ? (actionId: string, approved: boolean, notes: string): Promise<boolean> => {
                return new Promise<boolean>((resolve) => {
                    this.props.onApprovalRequest(uid, item.publicationId, actionId, approved, notes)
                        .then((result) => {
                            if (!result) this.props.snackbar.showMessage(<AppMessage resource="ApprovalProcessFailed" />, "error");
                            resolve(result);
                        })
                        .catch((error) => {
                            resolve(false);
                        });
                });
            }
            : null;

        const ignoreActionHandler = this.props.onIgnoreActionRequest
            ? (actionId: string): Promise<boolean> => {
                return new Promise<boolean>((resolve) => {
                    this.props.onIgnoreActionRequest(uid, item.publicationId, actionId)
                        .then((result) => {
                            if (!result) this.props.snackbar.showMessage(<AppMessage resource="ApprovalProcessFailed" />, "error");
                            resolve(result);
                        })
                        .catch((error) => {
                            resolve(false);
                        });
                });
            }
            : null;

        const sidebarHandler = this.props.onSidebarResetRequest
            ? () => this.props.onSidebarResetRequest()
            : null;

        const p: ICardProps = {

            item,

            feedType: this.props.feedType,

            showRelationsButton: true,

            onUpdateCard: (item: DeepPartial<FeedItemDto>) => {
                this.props.onUpdatePublication({ uid, items: [item] });
                this.recomputeRowHeight(rowIndex, true);
            },

            onExpandCardContent: (textExpanded: boolean): Promise<void> => {
                return new Promise<void>((resolve) => {
                    this.props.onExpandPublication(uid, item.publicationId, textExpanded)
                        .then(() => {
                            this.recomputeRowHeight(rowIndex, true);
                            resolve();
                        });
                });
            },

            onAddCard: addCardHandler,
            onRemoveCardFromFeed: removeCardFromFeedHandler,
            onBeforeEdit: beforeEditHandler,
            onBeforeDelete: beforeDeleteHandler,

            onChangePublicationVisibility: changePublicationVisibilityHandler,
            onPublishPublication: publishPublicationHandler,
            onReloadPublication: reloadHandler,
            onRemovePublication: removePublicationHandler,

            onApprovalRequest: approvalRequestHandler,
            onIgnoreActionRequest: ignoreActionHandler,
            onSidebarResetRequest: sidebarHandler,

            onCustomAction: customActionHandler
        };

        switch (item.publicationType) {
            case PublicationType.Answer:
                return <AnswerCard key={uid} classes={this.props.classes} {...p} />;
            case PublicationType.List:
                return <ListCard key={uid} classes={this.props.classes} {...p} />;
            case PublicationType.Poll:
                return <PollCard key={uid} classes={this.props.classes} {...p} />;
            case PublicationType.Post:
                return <PostCard key={uid} classes={this.props.classes} {...p} />;
            case PublicationType.Question:
                return <QuestionCard key={uid} classes={this.props.classes} {...p} />;
            case PublicationType.Course:
                return <CourseCard key={uid} classes={this.props.classes} {...p} />;
        }

        return null;
    }


    private buildFeedContent(data: FeedContentDto, rowIndex: number) {

        return data.items.map(
            (item, index) => {
                return item.interaction.visibility == PublicationVisility.Hidden && item.permissions.canClose
                    ? this.cardHidden(data.uid, item, rowIndex)
                    : this.cardFactory(data.uid, item, rowIndex);
            }
        );
    }


    componentDidUpdate(prevProps: Props) {
        const putNewItemOnTop = prevProps.feed.data.length > 0
            && this.props.feed.data.length > 0
            && prevProps.feed.data[0].uid != this.props.feed.data[0].uid;

        if (this.props.feed.reseting || putNewItemOnTop) {
            // Reseta o cache
            this.cache = new CellMeasurerCache({ fixedWidth: true, minHeight: 50 });
            if (this.infiniteLoaderRef) this.infiniteLoaderRef.resetLoadMoreRowsCache();

            if (this.props.feed.reseting) this.props.onGetFeed(0, this.getBatchSize() - 1);
            else if (putNewItemOnTop) this.recomputeRowHeight(0, false);
        }
        else if (this.props.feed.data.length !== prevProps.feed.data.length) {
            this.recomputeRowHeight(prevProps.feed.data.length, false);
        }
    }

    render() {
        const { onGetFeed } = this.props;
        const { data, hasMore, loading } = this.props.feed;

        return (
            <>
                {
                    this.props.onFeedReset &&
                    <Box display="none">
                        <a accessKey={AccessKeys.ReloadFeed} onClick={this.props.onFeedReset}><ReloadFeedText /></a>
                    </Box>
                }

                <RequestErrorDialog
                    open={this.state.lastError != null}
                    error={this.state.lastError}
                    onClose={() => this.setState({ lastError: null })}
                />

                <FocusOnFirstCard />

                <InfiniteLoader
                    ref={(ref) => this.infiniteLoaderRef = ref}
                    isRowLoaded={(index) => this.isRowLoaded(index.index)}
                    loadMoreRows={(params) => onGetFeed(params.startIndex, params.stopIndex + this.getBatchSize())}
                    rowCount={data.length + (hasMore ? 1 : 0)}
                >
                    {({ onRowsRendered, registerChild }) => (

                        <WindowScroller
                            scrollElement={window} >
                            {({ height, isScrolling, onChildScroll, scrollTop }) => (

                                <AutoSizer disableHeight >
                                    {({ width }) => {
                                        return (
                                            <>
                                                {
                                                    loading && data.length == 0 &&
                                                    <Box style={{ width: width }} mb={1}>
                                                        <LoadingIndicator />
                                                    </Box>
                                                }

                                                <List
                                                    ref={(ref) => { this.setRef(ref, registerChild); }}

                                                    autoHeight
                                                    height={height}
                                                    width={width}

                                                    overscanRowCount={0}
                                                    rowCount={data.length}

                                                    rowHeight={(p) => this.cache.rowHeight(p)}
                                                    rowRenderer={(p) => this.rowRenderer(p.index, p.key, p.parent, p.style)}
                                                    onRowsRendered={onRowsRendered}

                                                    scrollTop={scrollTop}
                                                    isScrolling={isScrolling}
                                                    onScroll={onChildScroll}
                                                    deferredMeasurementCache={this.cache}
                                                />

                                                {
                                                    loading && data.length > 0 &&
                                                    <SpinnerIndicator />
                                                }

                                            </>
                                        );
                                    }}
                                </AutoSizer>
                            )}
                        </WindowScroller>
                    )}
                </InfiniteLoader>
            </>
        );
    }
}

export default CardManager;
